// Section 19: Drought. 7% per season, Summer bias. For 2 seasons: water production 0.5x, forage
// 0.7x, crops 0.7x. Foreshadowed half the time ("the well is lower than last Summer"). Choices:
// Dig deeper (this season's builders dig instead of building, and water carriers work at full
// yield for the rest of the drought) or Ration (bear the shortfall).
// Constants: config.events.drought, config.eventDeck.foreshadowShare.
import type { Config } from '../../config';
import {
  choiceOutcome,
  effectActive,
  effectDelta,
  eventEntry,
  eventPrompt,
  eventTurn,
} from '../../core/content-helpers';
import { mod } from '../../core/modifiers';
import { isWorking } from '../../core/queries';
import { defineEvent, type EventChoiceDef } from '../../core/registry';
import type { ActiveEffect, Modifier, ReadContext } from '../../core/types';

function builders(ctx: ReadContext): number {
  let n = 0;
  for (const p of Object.values(ctx.state.people)) if (p.job === 'builder' && isWorking(p)) n++;
  return n;
}

function dry(ctx: ReadContext): ActiveEffect | undefined {
  return ctx.state.activeEffects.find(
    (e) => e.eventId === 'drought' && e.params.dug !== 1 && effectActive(ctx, e),
  );
}

export function drought(config: Config) {
  const c = config.events.drought;
  const choices: EventChoiceDef[] = [
    {
      id: 'dig-deeper',
      label: 'Dig deeper',
      preview: (ctx) => [{ key: 'drought-dig', params: { builders: builders(ctx) } }],
      apply: (ctx, prompt) => {
        const n = builders(ctx);
        const until = dry(ctx)?.untilTurn ?? ctx.season.turn;
        if (n === 0) return [choiceOutcome(prompt, 'dig-deeper', { builders: 0 })];
        return [
          effectDelta('drought', 'dug', ctx.season.turn, until - ctx.season.turn + 1, { dug: 1 }),
          choiceOutcome(prompt, 'dig-deeper', { builders: n }),
        ];
      },
    },
    {
      id: 'ration',
      label: 'Ration the water',
      preview: () => [{ key: 'drought-ration', params: { mul: c.waterMul } }],
      apply: (_ctx, prompt) => [choiceOutcome(prompt, 'ration')],
    },
  ];
  return defineEvent({
    id: 'drought',
    label: 'Drought',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['summer'],
    foreshadow: { chance: config.eventDeck.foreshadowShare },
    ledgerTag: null,
    weight: () => c.baseChance,
    text: () => eventEntry('drought', { seasons: c.seasons, water: c.waterMul }),
    apply: (ctx) => [
      effectDelta('drought', 'dry', eventTurn(ctx) + 1, c.seasons, {
        waterMul: c.waterMul,
        forageMul: c.forageMul,
        cropsMul: c.cropsMul,
      }),
      // M7: the prompt carries the headline's numbers (it read "Water will be x for seasons").
      eventPrompt(ctx, 'drought', choices, {
        defaultChoiceId: 'ration',
        params: { seasons: c.seasons, water: c.waterMul },
      }),
    ],
    choices,
    modifierTargets: ['harvestYield', 'output'],
    modifiers(ctx, target, effect) {
      const source = 'event:drought';
      const out: Modifier[] = [];
      if (effect.params.dug === 1) {
        if (
          target.kind === 'output' &&
          target.jobId === 'builder' &&
          ctx.season.turn === effect.sinceTurn
        )
          out.push(mod.mul(source, c.digBuilderOutputMul, 'digging deeper'));
        return out;
      }
      const p = effect.params;
      if (target.kind === 'harvestYield' && p.cropsMul !== undefined)
        out.push(mod.mul(source, p.cropsMul, 'drought'));
      if (target.kind !== 'output') return out;
      if (target.jobId === 'forager' && p.forageMul !== undefined)
        out.push(mod.mul(source, p.forageMul, 'drought'));
      const dug = ctx.state.activeEffects.some(
        (e) => e.eventId === 'drought' && e.params.dug === 1 && effectActive(ctx, e),
      );
      if (target.jobId === 'water-carrier' && p.waterMul !== undefined && !dug)
        out.push(mod.mul(source, p.waterMul, 'drought'));
      return out;
    },
  });
}
